import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { SITE } from "../config";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  // mouse tilt on cover
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 16);
    rx.set(-py * 16);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
      {/* radial gold glow */}
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(120% 90% at 78% 30%, rgba(216,171,78,0.22), transparent 55%)" }} />
      <div className="relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Text */}
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="font-grotesk uppercase tracking-[0.35em] text-xs md:text-sm text-[#d8ab4e] mb-8"
          >
            {SITE.tagline}
          </motion.p>

          <h1 className="font-display leading-[0.82] tracking-tight">
            {SITE.titleLines.map((t, i) => (
              <span key={t} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  className={`block text-[16vw] sm:text-[12vw] lg:text-[7vw] font-black ${
                    i === 1 ? "gold-text italic" : "text-[#f3ead6]"
                  }`}
                >
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, filter: "blur(12px)", y: 20 }}
            animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
            transition={{ delay: 1.5, duration: 1.1 }}
            className="mt-10 max-w-xl"
          >
            <p className="font-display italic text-2xl sm:text-3xl text-[#e7dcc2]">by {SITE.author}</p>
            <p className="mt-6 font-body text-base md:text-lg text-[#b9ad92] leading-relaxed">{SITE.intro}</p>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              <a
                href={SITE.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-buy-btn"
                className="group inline-flex items-center gap-3 bg-[#d8ab4e] text-[#0a0805] px-8 py-4 font-semibold uppercase tracking-widest hover:bg-[#f2c968] transition-colors"
              >
                {SITE.amazonLabel}
                <span className="inline-block transition-transform group-hover:translate-x-1.5">→</span>
              </a>
              <a href="#book" data-testid="hero-explore-btn" className="hover-underline uppercase tracking-[0.2em] text-sm text-[#e7dcc2]">
                Explore the book
              </a>
            </div>
            <p className="mt-5 text-xs uppercase tracking-[0.25em] text-[#7d7460]">Available in eBook & Paperback</p>
          </motion.div>
        </motion.div>

        {/* Cover */}
        <motion.div style={{ y: coverY }} className="lg:col-span-5 flex justify-center lg:justify-end" onMouseMove={onMove} onMouseLeave={onLeave}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000, "--cover": `url(${SITE.coverImage})` }}
            data-testid="hero-cover"
            className="cover-crop gold-glow w-[64vw] max-w-[380px] aspect-[7/10]"
          >
            <span className="sr-only">{SITE.title} cover</span>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 text-[11px] uppercase tracking-[0.3em] text-[#7d7460]"
      >
        Scroll ↓
      </motion.div>
    </section>
  );
};

export default Hero;
